import * as React from 'react'
import { Form } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import { FormattedMessage, useIntl } from 'react-intl'
import 'react-datepicker/dist/react-datepicker.css'
import { getLabel } from '../../../validators'
import { TimeFormats } from '../../models/DateTime/types'
import { transformDate } from '../../models/DateTime/utils'
import { getDatePickerValues } from './getDatePickerValues'
import { DescriptionBtnWithPopup } from '../../buttons/PromptBtn/DescriptionBtnWithPopup'
import { IErrMsg } from '../types'
import { memo } from 'react'
import { RequiredMark } from '../../ui/RequiredMark/RequiredMark'

export interface IDateTimePicker {
    name: string
    state: any
    valueKey: string
    messageKey: string
    schema?: any
    disabled?: boolean
    className?: string
    showTimeSelect?: boolean
    showTimeSelectOnly?: boolean
    timeIntervals?: number
    minDate?: Date
    maxDate?: Date
    timeSchema?: string
    timeFormat: string
    description?: string
    isColumn?: boolean
    isRequired?: boolean
    isLabelHidden?: boolean
    errMsg?: IErrMsg
    touched?: any
    setTouched?: (value: any) => void
    isParentDisabled?: boolean
    onEnter?: Function
    onBlur?: Function

    setState(newState: any): void
}

export const DateTimePicker = memo(function DateTimePicker({
    name,
    state,
    setState,
    valueKey,
    messageKey,
    schema,
    timeSchema,
    timeIntervals,
    className,
    description,
    isColumn,
    minDate,
    maxDate,
    disabled,
    isRequired,
    timeFormat,
    isLabelHidden,
    errMsg,
    touched,
    setTouched,
    isParentDisabled,
    onEnter,
    onBlur,
}: IDateTimePicker) {
    const intl = useIntl()

    const onChange = (date: Date): void => {
        setTouched && setTouched({ ...touched, [name]: true })

        const formattedDate = transformDate(timeFormat, date, timeFormat)
        setState({ ...state, [valueKey]: formattedDate })
    }

    const handleBlur = () => {
        if (typeof onBlur === 'function') onBlur()
        else setTouched && setTouched({ ...touched, [name]: true })
    }

    const handleKeyDown = (evt: any) => {
        if (evt.key === 'Enter' && typeof onEnter === 'function') {
            onEnter()
        }
    }

    const handleCalendarOpen = () => {
        setTimeout(() => {
            const header = document.querySelector(".react-datepicker-time__header")
            if (header) header.classList.add("mt-time-header")
        }, 0)
    }

    const datepickerValues = getDatePickerValues(
        timeFormat as TimeFormats,
        state,
        valueKey
    )
    const label = getLabel(messageKey)
    const isInvalid = touched && touched[name] && errMsg

    if (!datepickerValues || datepickerValues.unsupported) {
        return (
            <div
                style={{ maxWidth: '600px' }}
                className={`${className} d-flex flex-1 mt-1 mb-1 align-items-center justify-content-between`}
            >
                <Form.Label
                    style={{ maxWidth: '30%' }}
                    className={`label-main mt-1 text-capital`}
                >
                    {label}:{' '}
                </Form.Label>
                &nbsp;Unsupported datetime/time format
            </div>
        )
    }

    return (
        <Form.Group
            style={{ maxWidth: '600px' }}
            className={`${className} d-flex flex-1 mt-1 mb-1 align-items-baseline justify-content-between`}
        >
            {!isLabelHidden ? (
                <Form.Label
                    style={{ maxWidth: '30%' }}
                    className={`label-main mt-1 text-capital`}
                >
                    {label}: {isRequired && <RequiredMark />}
                </Form.Label>
            ) : null}
            <div
                style={{ width: isLabelHidden ? '100%' : '70%' }}
                className={`d-flex ${
                    !isColumn ? 'flex-1' : ''
                } p-relative flex-column`}
            >
                <div
                    className="d-flex justify-content-between w-100"
                >
                    <DatePicker
                        timeCaption="Time"
                        onCalendarOpen={handleCalendarOpen}
                        onChange={onChange}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        locale={intl.locale}
                        disabled={disabled ?? false}
                        minDate={datepickerValues.minDate}
                        maxDate={datepickerValues.maxDate}
                        minTime={datepickerValues.minTime}
                        maxTime={datepickerValues.maxTime}
                        timeIntervals={timeIntervals || 60}
                        selected={datepickerValues.selected}
                        dateFormat={datepickerValues.dateFormat}
                        timeFormat={datepickerValues.timeFormat}
                        showTimeSelect={timeFormat !== TimeFormats.DATE}
                        placeholderText={datepickerValues.placeholderText}
                        showTimeSelectOnly={datepickerValues.showTimeSelectOnly}
                        className={`form-control overflow-visible ${
                            isInvalid && !isParentDisabled && 'is-invalid'
                        }`}
                        wrapperClassName="w-100"
                        popperClassName="w-100"
                    />
                    {description && (
                        <DescriptionBtnWithPopup
                            type={'default'}
                            header={label}
                            description={description ?? ''}
                            className="mt-1 input-tooltip"
                        />
                    )}
                </div>
                {!isParentDisabled && isInvalid && (
                    <p
                        style={{
                            color: '#dc3545',
                            marginBottom: '0',
                            fontSize: '0.7rem',
                        }}
                    >
                        <FormattedMessage id={errMsg?.message} />
                    </p>
                )}
            </div>
        </Form.Group>
    )
})